import { Chip, Tooltip } from '@mui/material'
import {
  coalesce,
  coverageTypes,
  dateFormatter,
  formatCurrency,
  getToolTipTitle,
  ident,
  limitDecimals,
  lrpDateFormat,
  makeCoverageCommodityLabel,
  pages,
  formatRedshiftDate,
  coverageStatuses,
  zonedDate,
  gmt0timezone,
} from 'common'
import { format } from 'date-fns'
import { InsureIQStatusStyles } from '@harvestiq/constants'
import { InfoTooltip } from '../components/InfoTooltip'

export const calculationTypes = {
  cwt: 'cwt',
  head: 'head',
  total: 'total',
}

export const priceToggleOptions = {
  cash: 'cash',
  futures: 'futures',
}

const lgmCoverageSubTableColumns = [
  { field: 'marketingMonth', label: 'Marketing Month' },
  { field: 'numberofHead', label: 'Number of Head' },
  {
    field: 'premium',
    label: 'Premium',
    render: monthPremium => formatCurrency(monthPremium),
  },
  {
    field: 'subsidy',
    label: 'Subsidy',
    render: monthSubsidy => formatCurrency(monthSubsidy),
  },
  {
    field: 'grossMarginGuarantee',
    label: 'Gross Margin Guarantee',
    render: monthGrossMarginGuarantee =>
      formatCurrency(monthGrossMarginGuarantee),
  },
  {
    field: 'grossMargin',
    label: 'Gross Margin',
    render: (monthGrossMargin, { actualGrossMargin }) => (
      <Tooltip title={getToolTipTitle('grossMargin', actualGrossMargin)}>
        <span>{formatCurrency(monthGrossMargin)}</span>
      </Tooltip>
    ),
  },
  {
    field: 'indemnity',
    label: 'Probable Indemnity',
    render: indemnity => <span>{formatCurrency(indemnity)}</span>,
  },
]

export const calculationOptionsByPolicyType = {
  [coverageTypes.lrp]: [
    calculationTypes.cwt,
    calculationTypes.head,
    calculationTypes.total,
  ],
  [coverageTypes.lgm]: [calculationTypes.head, calculationTypes.total],
}

export const calculatorsByCalculationType = {
  cwt: () => ident,

  head: (targetWeight = 1) => {
    return value => {
      return limitDecimals(value * targetWeight)
    }
  },

  total: (targetWeight, numberOfHeads) => {
    return value => {
      return limitDecimals(value * numberOfHeads * targetWeight)
    }
  },
}

const calculateMarketingMonthValues = (marketingMonth, calculationType) => {
  const {
    subsidy,
    premium,
    actualIndemnity,
    expectedIndemnity,
    actualGrossMargin,
    expectedGrossMargin,
    numberofHead,
    grossMarginGuarantee,
  } = marketingMonth

  const calculator = calculatorsByCalculationType[calculationType](
    1,
    numberofHead,
  )

  return {
    ...marketingMonth,
    subsidy: calculator(subsidy),
    premium: calculator(premium),
    indemnity: calculator(coalesce(actualIndemnity, expectedIndemnity)),
    grossMargin: calculator(coalesce(actualGrossMargin, expectedGrossMargin)),
    grossMarginGuarantee: calculator(grossMarginGuarantee),
  }
}

const calculateLgmValues = (coverage, calculationType) => {
  const { marketingMonths, deductible } = coverage.details

  const subTable = {
    columns: lgmCoverageSubTableColumns,
    rows: marketingMonths.map(marketingMonth =>
      calculateMarketingMonthValues(marketingMonth, calculationType),
    ),
  }

  return {
    subTable,
    deductible,
  }
}

export const restructureCoverages = (
  coverages,
  priceType,
  calculationType = calculationTypes.head,
) => {
  return coverages.map(rawCoverage => {
    const coverage = { ...rawCoverage }
    const { details, totalValues } = coverage
    const {
      actualEndingValue,
      actualIndemnity,
      futuresIndemnity,
      coveragePrice,
      endDate,
      expectedEndingValue,
      expectedIndemnity,
      numberofHead = totalValues.numberofHeadTotal,
      premium,
      price,
      targetWeight,
    } = details

    const calculator = calculatorsByCalculationType[calculationType](
      targetWeight,
      numberofHead,
    )

    Object.assign(coverage, { targetWeight, numberofHead })

    if (coverage.type === coverageTypes.lgm) {
      Object.assign(coverage, calculateLgmValues(coverage, calculationType))
    } else {
      const indemnityByPriceMap = {
        [priceToggleOptions.cash]: actualIndemnity,
        [priceToggleOptions.futures]: futuresIndemnity,
      }

      const finalIndemnity =
        coverage.status === coverageStatuses.expired
          ? actualIndemnity
          : indemnityByPriceMap[priceType]

      Object.assign(coverage, {
        premium: calculator(premium),
        indemnity: calculator(coalesce(finalIndemnity, expectedIndemnity)),
        endingValue: calculator(
          coalesce(actualEndingValue, expectedEndingValue),
        ),
        cashPrice: price ? calculator(price.cash.closePrice) : 0,
        futuresPrice: price ? calculator(price.futures.closePrice) : 0,
        actualEndingValue: calculator(actualEndingValue),
        coveragePrice: calculator(coveragePrice),
        endDate,
      })
    }
    return { details, totalValues, ...coverage }
  })
}

export const statusColumn = {
  id: 'status',
  label: 'Status',
  isSortable: true,
  render: status => {
    return (
      <Chip label={status} color={InsureIQStatusStyles[status]} size="small" />
    )
  },
}

export const ownersColumn = {
  id: 'userId',
  label: 'Owner',
  render: (_id, { users = [], companies = [] }) => {
    const renderedValue = [...users, ...companies]
      .map(({ fullName, name }) => fullName || name)
      .join(', ')
    return renderedValue
  },
}

export const commodityColumn = {
  id: 'commodity',
  label: 'Commodity',
  isSortable: true,
  render: (commodity, { commodityType }) =>
    makeCoverageCommodityLabel(commodity, commodityType),
}

export const salesEffectiveDateColumn = {
  id: 'salesEffectiveDate',
  label: 'Sales Effective Date',
  render: salesEffectiveDate =>
    dateFormatter(salesEffectiveDate, lrpDateFormat),
}

export const endDateColumn = {
  id: 'endDate',
  label: (
    <InfoTooltip title="Specific Coverage Endorsement End Date indicates when the given LRP Position will close.">
      SCE End Date
    </InfoTooltip>
  ),
}

export const deductibleColumn = {
  id: 'deductible',
  label: 'Deductible',
  render: deductible => formatCurrency(deductible),
}

export const targetWeightColumn = {
  id: 'targetWeight',
  label: 'Target Weight',
}

export const numberofHeadColumn = {
  id: 'numberofHead',
  label: 'Total Number of Head',
}

export const premiumColumn = {
  id: 'premium',
  label: 'Premium',
  render: premium => formatCurrency(premium),
}

export const coveragePriceColumn = {
  id: 'coveragePrice',
  label: 'Coverage Price',
  render: coveragePrice => formatCurrency(coveragePrice),
}

export const endingValueColumn = {
  id: 'endingValue',
  label: (
    <InfoTooltip
      title={
        <>
          Latest relevant cash value. See the{' '}
          <a
            href={`${pages.faq.path}?faqs=what-are-the-calculations-for-actual-ending-value-and-expected-ending-value`}
            target="_blank"
            rel="noreferrer"
          >
            FAQ
          </a>{' '}
          for more detail.
        </>
      }
    >
      Cash Price
    </InfoTooltip>
  ),
  render: (endingValue, row) => {
    return <span>{formatCurrency(endingValue)}</span>
  },
}

export const generateCashPriceTooltip = values => {
  if (!values) {
    return ''
  }

  const { name, symbol, date } = values
  const textLine1 = `CME ${name || ''} Cash (${symbol})`
  const textLine2 = date ? `${formatRedshiftDate(date)} Close Price` : ''
  return (
    <>
      {textLine1} <br />
      {textLine2}
    </>
  )
}

export const generateFuturesPriceTooltip = values => {
  if (!values) {
    return ''
  }

  const { name, symbol, date, reportPeriod } = values
  const formattedDate = format(zonedDate(date, gmt0timezone), `LLL ''yy`)
  const textLine1 = `CME ${name || ''} ${formattedDate} Futures (${symbol})`
  const textLine2 = reportPeriod
    ? `${formatRedshiftDate(reportPeriod)} Close Price`
    : ''
  return (
    <>
      {textLine1} <br />
      {textLine2}
    </>
  )
}

export const cashPriceColumn = {
  id: 'cashPrice',
  label: (
    <InfoTooltip
      title={
        <>
          Latest relevant cash value. See the{' '}
          <a
            href={`${pages.faq.path}?faqs=what-are-the-calculations-for-actual-ending-value-and-expected-ending-value`}
            target="_blank"
            rel="noreferrer"
          >
            FAQ
          </a>{' '}
          for more detail.
        </>
      }
    >
      Cash Price
    </InfoTooltip>
  ),
  render: (cashPrice, { details }) => {
    if (!cashPrice) {
      return '-'
    }
    return (
      <Tooltip title={generateCashPriceTooltip(details?.price?.cash)}>
        <span>{formatCurrency(cashPrice)}</span>
      </Tooltip>
    )
  },
}

export const futuresPriceColumn = {
  id: 'futuresPrice',
  label: 'Futures Price',
  render: (futruesPrice, { details }) => {
    if (!futruesPrice) {
      return '-'
    }
    return (
      <Tooltip title={generateFuturesPriceTooltip(details?.price?.futures)}>
        <span>{formatCurrency(futruesPrice)}</span>
      </Tooltip>
    )
  },
}

export const actualEndingValueColumn = {
  id: 'actualEndingValue',
  label: 'Actual Ending Value',
  render: actualEndingValue => {
    return actualEndingValue ? formatCurrency(actualEndingValue) : '-'
  },
}

export const indemnityColumn = {
  id: 'indemnity',
  label: 'Probable Indemnity',
  render: indemnity => <span>{formatCurrency(indemnity)}</span>,
}

export const isLrpCoverageWatched = (coverage, quote) => {
  if (!coverage || !quote) {
    return false
  }
  return (
    coverage.commodity === quote.commodity &&
    coverage.commodityType === quote.commodityType &&
    coverage.details.endDate === quote.marketingDate &&
    coverage.details.coveragePrice === quote.coveragePrice &&
    coverage.details.coverageLevel === quote.coverageLevel &&
    coverage.details.premium === quote.producerPremium
  )
}
